import { Grid, Typography } from '@mui/material';
import { ProceedSaveLater, SubHeading } from './components';
import { Formik, validateYupSchema, yupToFormErrors } from 'formik';
import MFSelectField from '../../lib/formik/SelectField';
import { Applicant, Broker, BrokerList } from '../../redux-store/types/api-types';
import {
  AMC_APPROVER_CHECK_FOR_INDIVIDUAL,
  grossAnnualMasters,
  occupationDetailsMasters,
  PEPsMasters,
  USER_ROLES,
  DLCLMasters,
  DetailsDeclaration,
  ClientMasterDeclaration,
  DpInformationDeclaration,
  HoldingDeclaration,
  dateDematAccountDetails,
  nameOfBrokerMandatory,
  cityOfBirthAndOtherOccupationMandatory,
} from '../../utils/constant';
import {
  // eslint-disable-next-line
  allowOnlyNumbers,
  applicationComparison,
  applyRoleBasedStatus,
  getApplicantName,
  saveForLater,
  clearclIdField,
  cleardpIdField,
  dlclIdFieldMandatory,
  getclIdField,
  getdpIdField,
  checkValidationBasedOnDate,
  getDPCLIDFieldVal,
  pepandAmlCheck,
  otherBrokerNamePresent,
  currencyConversion,
  minNetworthDate,
} from '../../utils/utilityFunctions';
import { useDispatch, useSelector } from 'react-redux';
import { RootStateType } from '../../redux-store/reducers';
import { useEffect, useState } from 'react';
import { getBrokers, updateApplication } from '../../redux-store/actions/application';
import { useHistory } from 'react-router';
import { KYCDetailsSchema } from '../../utils/schema';
import { MFTextField } from '../../lib/formik';
import { useSnackbar } from 'notistack';
import { SearchableSelect } from '../../lib/formik/searchSelectField';
import MFCheckbox from '../../lib/formik/Checkbox';
import { showError } from '../../redux-store/actions/auth';
import { DatePicker } from '../../lib/formik/DatePicker';

type Values = {
  applicants: Partial<Applicant>[];
  saveType: string;
};

const initialValues: Values = {
  applicants: [
    {
      occupationDetails: '',
      otherOccupation: '',
      grossAnnualIncome: '',
      politicallyExposedPersonStatus: 'Not Applicable',
      ckycNo: '',
      dlclId: '',
      dpId: '',
      clId: '',
      nameOfBroker: '',
      otherNameOfBroker: '',
      detailsDeclaration: false,
      clientMasterDeclaration: false,
      dpInformationDeclaration: false,
      holdingDeclaration: false,
      netWorth: '',
      netWorthDate: '',
    },
  ],
  saveType: 'save and proceed',
};

const KYCDetails = ({
  values,
  applicant,
  index,
  createdAt,
}: {
  values: Values;
  applicant: string;
  index: number;
  createdAt: string;
}): JSX.Element => {
  const { role = '' } = useSelector((store: RootStateType) => store.auth);
  const { application } = useSelector((state: RootStateType) => state.application);
  return (
    <>
      <SubHeading>Additional KYC Details of {applicant} Applicant</SubHeading>
      <Grid item xs={12} sm={6}>
        <MFSelectField
          name={`applicants.${index}.occupationDetails`}
          label="Occupation Details *"
          items={occupationDetailsMasters.map((occupation) => ({
            key: occupation,
            value: occupation,
          }))}
        />
      </Grid>
      {values?.applicants[index].occupationDetails === 'OTHERS' && (
        <Grid item xs={12} sm={6}>
          <MFTextField
            name={`applicants.${index}.otherOccupation`}
            label={`Please specify Occupation ${
              checkValidationBasedOnDate(createdAt, cityOfBirthAndOtherOccupationMandatory)
                ? '*'
                : ''
            }`}
            placeholder="Enter Occupation"
          />
        </Grid>
      )}
      <Grid item xs={12} sm={6}>
        <MFSelectField
          name={`applicants.${index}.grossAnnualIncome`}
          label="Gross Annual Income *"
          items={grossAnnualMasters.map((grossIncome) => ({
            key: grossIncome,
            value: grossIncome,
          }))}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <MFTextField
          name={`applicants.${index}.netWorth`}
          label="Net Worth"
          placeholder="Enter Net Worth"
        />
        {values.applicants[index].netWorth && !isNaN(Number(values.applicants[index].netWorth)) && (
          <Typography sx={{ fontSize: 13 }}>
            {currencyConversion(values.applicants[index].netWorth)}
          </Typography>
        )}
      </Grid>
      <Grid item xs={12} sm={6}>
        <DatePicker
          label={'Net worth as on'}
          inputLabelStyles={{
            transform: 'unset',
            fontSize: 14,
            fontWeight: 500,
            color: 'rgba(0,0,0,0.7)',
          }}
          placeholder={'Enter Net worth as on'}
          name={`applicants.${index}.netWorthDate`}
          value={values.applicants[index].netWorthDate}
          minDate={minNetworthDate(application?.createdAt || '')}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <MFSelectField
          name={`applicants.${index}.politicallyExposedPersonStatus`}
          label="Politically Exposed Person (PEP) Status *"
          items={PEPsMasters.map((pep) => ({ key: pep, value: pep }))}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <MFTextField
          name={`applicants.${index}.ckycNo`}
          label="CKYC Number *"
          placeholder={'Enter CKYC number'}
          // type="number"
          // onKeyDown={(e) => {
          //   allowOnlyNumbers(e);
          // }}
          // trimOnBlur={false}
          disabled={[USER_ROLES.INVESTOR, USER_ROLES.POAAPPROVER].includes(role)}
        />
      </Grid>
    </>
  );
};

export const DematDeclaration = ({ index }: { index: number }): JSX.Element => {
  return (
    <>
      <Grid item xs={12} sx={{ mt: 1 }}>
        <MFCheckbox
          name={`applicants.${index}.detailsDeclaration`}
          label={DetailsDeclaration}
          sx={{
            fontSize: 14,
            fontWeight: 500,
            color: 'rgba(0,0,0,0.7)',
            alignItems: 'flex-start',
            '.MuiTypography-root': {
              fontSize: 15,
              fontWeight: 500,
            },
          }}
        />
      </Grid>
      <Grid item xs={12} sx={{ mt: 1 }}>
        <MFCheckbox
          name={`applicants.${index}.clientMasterDeclaration`}
          label={ClientMasterDeclaration}
          sx={{
            fontSize: 14,
            fontWeight: 500,
            color: 'rgba(0,0,0,0.7)',
            alignItems: 'flex-start',
            '.MuiTypography-root': {
              fontSize: 15,
              fontWeight: 500,
            },
          }}
        />
      </Grid>
      <Grid item xs={12} sx={{ mt: 1 }}>
        <MFCheckbox
          name={`applicants.${index}.dpInformationDeclaration`}
          label={DpInformationDeclaration}
          sx={{
            fontSize: 14,
            fontWeight: 500,
            color: 'rgba(0,0,0,0.7)',
            alignItems: 'flex-start',
            '.MuiTypography-root': {
              fontSize: 15,
              fontWeight: 500,
            },
          }}
        />
      </Grid>
      <Grid item xs={12} sx={{ mt: 1 }}>
        <MFCheckbox
          name={`applicants.${index}.holdingDeclaration`}
          label={HoldingDeclaration}
          sx={{
            fontSize: 14,
            fontWeight: 500,
            color: 'rgba(0,0,0,0.7)',
            alignItems: 'flex-start',
            '.MuiTypography-root': {
              fontSize: 15,
              fontWeight: 500,
            },
          }}
        />
      </Grid>
    </>
  );
};

export const DematDetails = ({
  values,
  index,
  brokers,
  disabled,
}: {
  values: Values;
  index: number;
  brokers: Broker[];
  disabled: boolean;
}): JSX.Element => {
  const { application } = useSelector((store: RootStateType) => store.application);
  const { createdAt = '' } = application || {};

  return (
    <>
      {/* <SubHeading>Demat Account details</SubHeading> */}
      <Grid item xs={12} sm={6}>
        <MFSelectField
          name={`applicants.${index}.dlclId`}
          label={`Demat Account Details (for allotment of units) ${
            checkValidationBasedOnDate(createdAt, dateDematAccountDetails) || dlclIdFieldMandatory()
              ? '*'
              : ''
          }`}
          items={getDPCLIDFieldVal(DLCLMasters, createdAt).map((Id) => ({
            key: DLCLMasters[Id],
            value: Id,
          }))}
          disabled={disabled}
        />
      </Grid>
      {getdpIdField(values.applicants[index].dlclId || '') && (
        <Grid item xs={12} sm={6}>
          <MFTextField
            name={`applicants.${index}.dpId`}
            label="DP ID *"
            placeholder="Enter DP ID"
            disabled={disabled}
          />
        </Grid>
      )}
      {getclIdField(values.applicants[index].dlclId || '') && (
        <Grid item xs={12} sm={6}>
          <MFTextField
            name={`applicants.${index}.clId`}
            label="CL ID *"
            placeholder="Enter CL ID"
            disabled={disabled}
          />
        </Grid>
      )}
      {getdpIdField(values.applicants[index].dlclId || '') && (
        <Grid item xs={12} sm={6}>
          <SearchableSelect
            name={`applicants.${index}.nameOfBroker`}
            label={`Name of Broker/Depository Participant (DP) ${
              checkValidationBasedOnDate(createdAt, nameOfBrokerMandatory) ? '*' : ''
            }`}
            items={brokers?.map((broker: Broker) => ({
              key: broker.value,
              value: broker.key,
            }))}
            searchFieldPlaceholder={'Search Broker/Depository Participant'}
          />
        </Grid>
      )}
      {getdpIdField(values.applicants[index].dlclId || '') &&
        otherBrokerNamePresent(values.applicants[index].nameOfBroker || '') && (
          <Grid item xs={12} sm={6}>
            <MFTextField
              name={`applicants.${index}.otherNameOfBroker`}
              label="Other Broker Name *"
              placeholder="Enter Other Broker Name"
            />
          </Grid>
        )}
    </>
  );
};

export default function AdditionalKYCDetails(): JSX.Element {
  const [kycDetails, setkycDetails] = useState(initialValues);
  const { application } = useSelector((store: RootStateType) => store.application);
  const { role = '' } = useSelector((store: RootStateType) => store.auth);
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const [brokerList, setBrokerList] = useState<BrokerList>();

  useEffect(() => {
    (async function () {
      try {
        const response = (await dispatch(getBrokers())) as unknown as BrokerList;
        setBrokerList(response as BrokerList);
      } catch (e) {
        console.error((e as Error).message);
      }
    })();
  }, []);

  useEffect(() => {
    const { applicants: exisitingApplicants = [], createdAt = '' } = application || {};
    setkycDetails({
      ...kycDetails,
      applicants: exisitingApplicants.map((applicant) => ({
        occupationDetails: applicant.occupationDetails || '',
        otherOccupation: applicant.otherOccupation || '',
        grossAnnualIncome: applicant.grossAnnualIncome || '',
        politicallyExposedPersonStatus: applicant.politicallyExposedPersonStatus
          ? applicant.politicallyExposedPersonStatus
          : [USER_ROLES.INVESTOR, USER_ROLES.POAAPPROVER].includes(role)
          ? ''
          : 'Not Applicable',
        ckycNo: applicant.ckycNo || '',
        dlclId: applicant.dlclId
          ? applicant.dlclId
          : checkValidationBasedOnDate(createdAt, dateDematAccountDetails) || dlclIdFieldMandatory()
          ? ''
          : 'none',
        dpId: applicant.dpId || '',
        clId: applicant.clId || '',
        nameOfBroker: applicant.nameOfBroker || '',
        otherNameOfBroker: applicant.otherNameOfBroker || '',
        detailsDeclaration: applicant.detailsDeclaration || false,
        clientMasterDeclaration: applicant.clientMasterDeclaration || false,
        dpInformationDeclaration: applicant.dpInformationDeclaration || false,
        holdingDeclaration: applicant.holdingDeclaration || false,
        
              netWorth: applicant.netWorth || '',
              netWorthDate: applicant.netWorthDate || '',
      })),
    });
  }, [application]);

  const handleSubmit = async (values: Values) => {
    try {
      const { applicants, saveType } = values;
      const {
        applicants: exisitingApplicants = [],
        id,
        applicant1ReferenceId = '',
        currentStep,
        status,
        hasPOA,
        applicationNumber,
      } = application || {};

      const updatedApplicants = exisitingApplicants.map((applicant, index) => {
        if (
          applicants[index].detailsDeclaration === false ||
          applicants[index].clientMasterDeclaration === false ||
          applicants[index].dpInformationDeclaration === false ||
          applicants[index].holdingDeclaration === false
        ) {
          throw `Declaration is required for ${getApplicantName(index + 1)} Applicant`;
        }
        return {
          ...applicant,
          ...applicants[index],
          otherOccupation:
            applicants[index].occupationDetails === 'OTHERS'
              ? applicants[index].otherOccupation
              : '',
          clId: clearclIdField(applicants[index].dlclId || '') ? '' : applicants[index].clId,
          dpId: cleardpIdField(applicants[index].dlclId || '') ? '' : applicants[index].dpId,
          nameOfBroker: cleardpIdField(applicants[index].dlclId || '')
            ? ''
            : applicants[index].nameOfBroker,
          otherNameOfBroker: otherBrokerNamePresent(applicants[index].nameOfBroker || '')
            ? applicants[index].otherNameOfBroker
            : '',
          netWorthDate: applicants[index].netWorthDate ? applicants[index].netWorthDate : null,
        };
      });
      const checkApplication = applicationComparison(application, {
        ...application,
        applicants: updatedApplicants,
        currentStep: !!currentStep && currentStep > 3 ? currentStep : Number(currentStep) + 1,
      });
      const isSaveLater = saveType !== 'save and proceed';
      if (id && !checkApplication) {
        setLoading(true);
        await dispatch(
          updateApplication({
            body: {
              ...application,
              applicants: updatedApplicants,
              status:
                !hasPOA &&
                (AMC_APPROVER_CHECK_FOR_INDIVIDUAL ||
                  pepandAmlCheck(application?.applicants || [])) &&
                status !== 'draft' &&
                applyRoleBasedStatus(role)
                  ? 'sent_to_amc_approver'
                  : status,
              currentStep: 4,
              //!!currentStep && currentStep > 3 ? currentStep : Number(currentStep) + 1,
            },
            applicationId: id,
            ...(isSaveLater && { toastMessage: '' }),
          })
        );
        !isSaveLater
          ? history.push('fatca', { id, applicant1ReferenceId })
          : history.push(saveForLater(role, id, applicant1ReferenceId));
      } else if (checkApplication) {
        if (isSaveLater) {
          enqueueSnackbar(`Application ${applicationNumber} - ` + ' Saved successfully', {
            variant: 'success',
            autoHideDuration: 3000,
          });
        }
        !isSaveLater
          ? history.push('fatca', { id, applicant1ReferenceId })
          : history.push(saveForLater(role, id, applicant1ReferenceId));
      }
    } catch (e) {
      setLoading(false);
      typeof e === 'string' && dispatch(showError(e));
      console.error((e as Error).message);
    }
  };
  const isFieldDisabled = [USER_ROLES.INVESTOR, USER_ROLES.POAAPPROVER].includes(role);
  return (
    <Formik
      initialValues={kycDetails}
      onSubmit={handleSubmit}
      validate={(values: Values) => {
        try {
          validateYupSchema(
            values,
            KYCDetailsSchema(brokerList?.broker_list as Broker[], application?.createdAt || ''),
            true,
            values
          );
        } catch (e) {
          return yupToFormErrors(e);
        }
      }}
      enableReinitialize={true}>
      {({ handleSubmit, values, setValues }) => (
        <Grid
          container
          rowSpacing={1}
          // columnSpacing={5}
          sx={{
            width: '100%',
            ml: 0,
            '.MuiGrid-item': { px: { xs: 0, sm: '30px' } },
          }}
          component="form"
          noValidate
          onSubmit={handleSubmit}>
          {values.applicants.map((_, index) => (
            <>
              <KYCDetails
                values={values}
                applicant={getApplicantName(index + 1)}
                key={index}
                index={index}
                createdAt={application?.createdAt || ''}
              />
              <DematDetails
                values={values}
                index={index}
                brokers={brokerList?.broker_list as Broker[]}
                disabled={isFieldDisabled || false}
              />
              <DematDeclaration index={index} />
            </>
          ))}
          <ProceedSaveLater
            saveLater={() => {
              setValues({
                ...values,
                saveType: 'save for later',
              });
            }}
            saveAndProceed={() => {
              setValues({
                ...values,
                saveType: 'save and proceed',
              });
            }}
            loader={loading}
            clickedButton={values.saveType}
          />
        </Grid>
      )}
    </Formik>
  );
}
